import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect } from 'react';
import { useQueryState } from 'nuqs';
import { readStoredParam, type StoredParam, stampParam } from './persisted-param-storage';
import { readCookie } from './read-cookie';
import { useSafeLocalStorage } from './use-safe-local-storage';
function cookieStillHoldsStoredValue(cookieName: string | undefined, rawValue: string): boolean {
  return Boolean(cookieName && rawValue && readCookie(cookieName) === rawValue);
}
export interface PersistedParamState {
  setValue: (value: string) => void;
  value: string;
}
interface PersistedQueryParamOptions {
  /** First-party cookie to persist when the URL does not carry this param. */
  cookieFallback?: string;
}

/**
 * URL query param with localStorage persistence. URL wins when present;
 * otherwise the stored value (and optional cookie) is used, until it ages past
 * `PERSISTED_PARAM_TTL_MS`.
 *
 * Click IDs are case-sensitive per ad-platform specs — do not normalize keys.
 */
export function usePersistedQueryParam(queryKey, storageKey, options) {
  const $ = _c(24);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      defaultValue: ""
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [urlValue] = useQueryState(queryKey, t0);
  const [stored, setStored] = useSafeLocalStorage(storageKey, "");
  const cookieFallback = options?.cookieFallback;
  let t1;
  if ($[1] !== stored) {
    t1 = readStoredParam(stored, Date.now());
    $[1] = stored;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    value: storedValue,
    rawValue,
    isExpired,
    isUnstamped
  } = t1;
  let t2;
  if ($[3] !== setStored) {
    t2 = value => setStored(stampParam(value, Date.now()));
    $[3] = setStored;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const persist = t2;
  let t3;
  let t4;
  if ($[5] !== cookieFallback || $[6] !== isExpired || $[7] !== isUnstamped || $[8] !== persist || $[9] !== rawValue || $[10] !== setStored || $[11] !== storedValue || $[12] !== urlValue) {
    t3 = () => {
      if (urlValue && urlValue !== storedValue) {
        persist(urlValue);
        return;
      }
      if (isExpired) {
        if (!cookieStillHoldsStoredValue(cookieFallback, rawValue)) {
          setStored("");
        }
        return;
      }
      if (isUnstamped) {
        setStored(stampParam(storedValue, Date.now()));
      }
    };
    t4 = [urlValue, storedValue, rawValue, isExpired, isUnstamped, cookieFallback, persist, setStored];
    $[5] = cookieFallback;
    $[6] = isExpired;
    $[7] = isUnstamped;
    $[8] = persist;
    $[9] = rawValue;
    $[10] = setStored;
    $[11] = storedValue;
    $[12] = urlValue;
    $[13] = t3;
    $[14] = t4;
  } else {
    t3 = $[13];
    t4 = $[14];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[15] !== cookieFallback || $[16] !== persist || $[17] !== rawValue || $[18] !== urlValue) {
    t5 = () => {
      if (!cookieFallback || urlValue) {
        return;
      }
      const cookieValue = readCookie(cookieFallback);
      if (cookieValue && cookieValue !== rawValue) {
        persist(cookieValue);
      }
    };
    t6 = [cookieFallback, urlValue, rawValue, persist];
    $[15] = cookieFallback;
    $[16] = persist;
    $[17] = rawValue;
    $[18] = urlValue;
    $[19] = t5;
    $[20] = t6;
  } else {
    t5 = $[19];
    t6 = $[20];
  }
  useEffect(t5, t6);
  const t7 = urlValue || storedValue || "";
  let t8;
  if ($[21] !== persist || $[22] !== t7) {
    t8 = {
      value: t7,
      setValue: persist
    };
    $[21] = persist;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}

/**
 * Cookie-only persistence (no URL form). Used for TikTok `_ttp`.
 */
export function usePersistedCookieParam(cookieName, storageKey) {
  const $ = _c(16);
  const [stored, setStored] = useSafeLocalStorage(storageKey, "");
  let t0;
  if ($[0] !== stored) {
    t0 = readStoredParam(stored, Date.now());
    $[0] = stored;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    value: storedValue,
    rawValue,
    isExpired,
    isUnstamped
  } = t0;
  let t1;
  if ($[2] !== setStored) {
    t1 = value => setStored(stampParam(value, Date.now()));
    $[2] = setStored;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const persist = t1;
  let t2;
  let t3;
  if ($[4] !== cookieName || $[5] !== isExpired || $[6] !== isUnstamped || $[7] !== persist || $[8] !== rawValue || $[9] !== setStored || $[10] !== storedValue) {
    t2 = () => {
      const cookieValue = readCookie(cookieName);
      if (cookieValue && cookieValue !== rawValue) {
        persist(cookieValue);
        return;
      }
      if (isExpired) {
        if (!cookieValue) {
          setStored("");
        }
        return;
      }
      if (isUnstamped) {
        setStored(stampParam(storedValue, Date.now()));
      }
    };
    t3 = [cookieName, storedValue, rawValue, isExpired, isUnstamped, persist, setStored];
    $[4] = cookieName;
    $[5] = isExpired;
    $[6] = isUnstamped;
    $[7] = persist;
    $[8] = rawValue;
    $[9] = setStored;
    $[10] = storedValue;
    $[11] = t2;
    $[12] = t3;
  } else {
    t2 = $[11];
    t3 = $[12];
  }
  useEffect(t2, t3);
  const t4 = storedValue || "";
  let t5;
  if ($[13] !== persist || $[14] !== t4) {
    t5 = {
      value: t4,
      setValue: persist
    };
    $[13] = persist;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}